import { createBrowserRouter, type RouteObject } from 'react-router';
import { FullPageLoader } from '@/components/StateViews';
import { AppLayout } from '@/layout/AppLayout';
import { NotFoundPage, RouteErrorPage } from '@/routes/ErrorPages';
import { GuestOnly, RequireAuth } from '@/routes/guards';

// Each page is its own chunk, loaded when first visited.
const page = (load: () => Promise<{ default: React.ComponentType }>) => async () => ({
  Component: (await load()).default,
});

export const routes: RouteObject[] = [
  {
    errorElement: <RouteErrorPage />,
    // Shown while the first page's code loads.
    hydrateFallbackElement: <FullPageLoader />,
    children: [
      {
        element: <GuestOnly />,
        children: [
          { path: '/login', lazy: page(() => import('@/features/auth/pages/LoginPage')) },
          { path: '/forgot-password', lazy: page(() => import('@/features/auth/pages/ForgotPasswordPage')) },
        ],
      },
      // Reached from email links; usable whether or not someone is signed in.
      { path: '/reset-password', lazy: page(() => import('@/features/auth/pages/ResetPasswordPage')) },
      { path: '/accept-invite', lazy: page(() => import('@/features/auth/pages/AcceptInvitePage')) },
      {
        element: <RequireAuth />,
        children: [
          {
            element: <AppLayout />,
            errorElement: <RouteErrorPage />,
            children: [
              { index: true, lazy: page(() => import('@/features/dashboard/pages/DashboardPage')) },
              { path: '/leads', lazy: page(() => import('@/features/leads/pages/LeadsPage')) },
              { path: '/leads/trash', lazy: page(() => import('@/features/leads/pages/TrashPage')) },
              { path: '/leads/:id', lazy: page(() => import('@/features/leads/pages/LeadDetailPage')) },
              { path: '/companies', lazy: page(() => import('@/features/companies/pages/CompaniesPage')) },
              { path: '/users', lazy: page(() => import('@/features/users/pages/UsersPage')) },
              { path: '/account', lazy: page(() => import('@/features/account/pages/AccountPage')) },
              { path: '*', element: <NotFoundPage /> },
            ],
          },
        ],
      },
    ],
  },
];

export const createRouter = () => createBrowserRouter(routes);
